import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import LocationOnIcon from "@mui/icons-material/LocationOnOutlined";
import { Box, Divider, lighten, Stack, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import type {
  BookingDetails as BookingDetailsType,
  CampgroundDataResponse,
  PriceComponent,
} from "@/types/api";
import { formatCampgroundAddress, getCountryByCode } from "@/utils/address";
import { getCampsiteTypeLabel, getDateFnsLocale } from "@/utils/booking";
import { getExtraPeriodLabel, isNoPriceBillingType } from "@/utils/extras";
import { formatCurrency, groupComponentsByName } from "@/utils/pricing";

interface BookingDetailsProps {
  bookingDetails: BookingDetailsType;
  campgroundData: CampgroundDataResponse;
}

export default function BookingDetails({ bookingDetails, campgroundData }: BookingDetailsProps) {
  const { i18n } = useLingui();
  const theme = useTheme();
  const dateFnsLocale = getDateFnsLocale(i18n.locale);

  const formatPrice = (amount: number) =>
    formatCurrency(amount, bookingDetails.pricelist.currency, i18n.locale);

  const dateFrom = new Date(bookingDetails.booking_data.date_from);
  const dateUntil = new Date(bookingDetails.booking_data.date_until);

  // Find campsite details from campground data
  const firstCampsiteId = bookingDetails.booking_data.campsites[0]?.id;
  const matchedCampsite = campgroundData.campground?.campsites?.find(
    (c) => c.id === firstCampsiteId,
  );
  const campsiteImageUrl = matchedCampsite?.images?.[0]?.variants?.["4_3"]?.url;
  const campsiteTypeLabel = getCampsiteTypeLabel(
    matchedCampsite?.campsite_type ?? null,
    campgroundData.campsite_types,
  );

  // Group price components
  const allComponents = bookingDetails.pricelist.components || [];
  const componentGroups = groupComponentsByName(allComponents);

  const campsiteGroups = componentGroups.filter((group) => group[0]?.object_type === "campsite");

  const personComponents = allComponents.filter((c) => c.object_type === "person");
  const personSeasonGroups: PriceComponent[][] = [];
  const personSeasonMap = new Map<string, PriceComponent[]>();
  personComponents.forEach((component) => {
    const key = component.season_name ?? "";
    if (!personSeasonMap.has(key)) {
      const group: PriceComponent[] = [component];
      personSeasonMap.set(key, group);
      personSeasonGroups.push(group);
    } else {
      personSeasonMap.get(key)?.push(component);
    }
  });

  const extraGroups = componentGroups.filter((group) => group[0]?.object_type === "extra");

  const getComponentPriceLabel = (component: PriceComponent): string => {
    if (
      component.object_type === "extra" &&
      isNoPriceBillingType(component.extra_fields?.billing_type ?? "")
    ) {
      return getExtraPeriodLabel(
        component.extra_fields?.billing_type ?? "",
        i18n,
        component.amount,
      );
    }
    if (component.included) {
      return t`Included`;
    }
    return formatPrice(component.price);
  };

  const cardSx = {
    border: 1,
    borderColor: "divider",
    borderRadius: 5,
    bgcolor: "background.paper",
    overflow: "hidden",
  };

  return (
    <Stack spacing={2}>
      {/* Card 1: Campground Info */}
      <Box sx={{ ...cardSx, display: "flex" }}>
        {/* Left: campsite image spanning full card height */}
        {campsiteImageUrl && (
          <Box
            sx={{
              width: 200,
              flexShrink: 0,
            }}
          >
            <img
              src={campsiteImageUrl}
              alt={campgroundData.campground?.name || ""}
              crossOrigin="anonymous"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        )}

        {/* Right: name/location top, stats bottom */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>
              {campgroundData.campground?.name}
            </Typography>
            <Stack
              sx={{
                bgcolor: "background.default",
                borderRadius: 5,
                px: 2,
                py: 0.5,
                width: "fit-content",
              }}
              direction="row"
              alignItems="center"
            >
              <LocationOnIcon />
              <Typography variant="body2">
                {formatCampgroundAddress(campgroundData.campground, campgroundData.countries)}
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              // mt: "auto",
              mx: 2,
              display: "flex",
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Box sx={{ flex: 1, px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                <Trans>Check-In</Trans>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {format(dateFrom, "MMM d, yyyy", { locale: dateFnsLocale })}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                <Trans>Check-Out</Trans>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {format(dateUntil, "MMM d, yyyy", { locale: dateFnsLocale })}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                <Trans>Guests</Trans>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {bookingDetails.num_persons}
              </Typography>
            </Box>
            {campsiteTypeLabel && (
              <Box sx={{ flex: 1, px: 2, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  <Trans>Type</Trans>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {campsiteTypeLabel}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Card 2: Personal Info + Travellers + Vehicle + Message */}
      <Stack sx={{ ...cardSx, p: 2 }} divider={<Divider />} spacing={2}>
        {/* Section B: Personal Info */}
        <Stack direction="row" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <Trans>Booker</Trans>
            </Typography>
            <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
              {bookingDetails.first_name} {bookingDetails.last_name}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <Trans>Address</Trans>
            </Typography>
            <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
              {bookingDetails.street1}
            </Typography>
            {bookingDetails.street2 && (
              <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
                {bookingDetails.street2}
              </Typography>
            )}
            <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
              {bookingDetails.zip_code} {bookingDetails.city}
            </Typography>
            <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
              {getCountryByCode(bookingDetails.country, campgroundData.countries)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <Trans>Phone</Trans>
            </Typography>
            <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
              {bookingDetails.phone}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <Trans>Email</Trans>
            </Typography>
            <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
              {bookingDetails.email}
            </Typography>
          </Box>
        </Stack>

        {/* Section C: Travellers + Vehicle */}
        <Stack direction="row" justifyContent="flex-start" spacing={10}>
          {bookingDetails.guests.map((guest, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: guests have no stable ID
            <Box key={index}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <Trans>Traveller</Trans> {index + 1}
              </Typography>
              <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
                {guest.first_name} {guest.last_name}
              </Typography>
              <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
                {format(new Date(guest.birthdate), "MMM d, yyyy", {
                  locale: dateFnsLocale,
                })}
              </Typography>
            </Box>
          ))}
          {bookingDetails.license_plate && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <Trans>Vehicle details</Trans>
              </Typography>
              <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
                {bookingDetails.license_plate}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Section D: Message */}
        {bookingDetails.message && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <Trans>Your message to the campsite</Trans>
            </Typography>
            <Typography variant="body2" className="ph-mask" sx={{ fontWeight: 500 }}>
              {bookingDetails.message}
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Card 3: Price Breakdown */}
      <Box sx={cardSx}>
        {/* Section E: Pricing */}
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <Trans>Accommodation details:</Trans>
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {campsiteGroups.map((group) => {
              const first = group[0]!;
              return (
                <Box key={`campsite-group-${first.name}-${first.included}`}>
                  {group.map((component, idx) => {
                    const compDateFrom = component.date_from
                      ? new Date(component.date_from)
                      : dateFrom;
                    const compDateUntil = component.date_until
                      ? new Date(component.date_until)
                      : dateUntil;
                    return (
                      // biome-ignore lint/suspicious/noArrayIndexKey: components within a group have no stable ID
                      <Box key={`campsite-${idx}`} sx={{ mb: 0.5 }}>
                        {group.length > 1 && (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {format(compDateFrom, "MMM d, yyyy", {
                              locale: dateFnsLocale,
                            })}
                            –
                            {format(compDateUntil, "MMM d, yyyy", {
                              locale: dateFnsLocale,
                            })}{" "}
                            ({t`nights`}: {component.days})
                          </Typography>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2">{component.name}</Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            {component.included ? t`Included` : formatPrice(component.price)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Stack>

          <Divider />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
            <Trans>Guest details:</Trans>
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {personSeasonGroups.map((group, groupIdx) => {
              const first = group[0]!;
              const groupDateFrom = first.date_from ? new Date(first.date_from) : dateFrom;
              const groupDateUntil = first.date_until ? new Date(first.date_until) : dateUntil;
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: person season groups have no stable ID
                <Box key={`person-season-${groupIdx}`}>
                  {personSeasonGroups.length > 1 && (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {format(groupDateFrom, "MMM d, yyyy", {
                        locale: dateFnsLocale,
                      })}
                      –
                      {format(groupDateUntil, "MMM d, yyyy", {
                        locale: dateFnsLocale,
                      })}{" "}
                      ({t`nights`}: {first.days})
                    </Typography>
                  )}
                  {group.map((component, idx) => (
                    <Box
                      // biome-ignore lint/suspicious/noArrayIndexKey: components within a group have no stable ID
                      key={`person-${groupIdx}-${idx}`}
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">
                        {component.amount} x {component.name}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        {getComponentPriceLabel(component)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Stack>
        </Box>
        {/* Subtotal */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            bgcolor: lighten(theme.palette.primary.main, 0.85),
            p: 2,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            <Trans>Subtotal</Trans>
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatPrice(bookingDetails.pricelist.subtotal)}
          </Typography>
        </Stack>

        {/* Section F: Extras */}
        {extraGroups.length > 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
              <Trans>Extras</Trans>
            </Typography>
            <Stack spacing={0.5}>
              {extraGroups.map((group) => {
                const first = group[0]!;
                if (group.length === 1) {
                  return (
                    <Box
                      key={`extra-${first.name}-${first.included}`}
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">{first.description}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap", ml: 2 }}>
                        {getComponentPriceLabel(first)}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box key={`extra-group-${first.name}-${first.included}`}>
                    {group.map((component) => (
                      <Box
                        key={`extra-${component.object_id}`}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">{component.description}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "nowrap", ml: 2 }}>
                          {getComponentPriceLabel(component)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Section G: Total */}
        <Box
          sx={{
            display: "flex",
            py: 2,
            px: 3,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Typography variant="body1" sx={{ flex: 1, fontWeight: 500 }}>
            <Trans>Total</Trans>
          </Typography>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {formatPrice(bookingDetails.pricelist.total)}
            </Typography>
            <Typography variant="body2">
              <Trans>incl. local taxes</Trans>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}
